import { bind, classNames, h, signal, tag } from "omi";

import { useValidations } from "@/wc/pages/project/store/queries/useValidations.ts";
import { NWButton } from "@/wc/components/atoms/nw-button";
import { NWError } from "@/wc/components/base/nw-error";
import { triggerToast } from "@/wc/components/base/nw-toast";
import { openLoginModal, openProModal } from "@/wc/components/modals";
import { useEditorController } from "@/wc/editor/useEditor.ts";
import globalStyles from "@/globals/styles.css?inline";
import { ErrorService } from "@/services/errorService";
import { PureComponent } from "@/utils/pureComponent.tsx";
import { networkSignal } from "@/utils/signals";
import { Store } from "@/store";
import { tailwind } from "@/tailwind";
import { initApi } from "@/utils";

import { NWSectionStatus, ProVariantProps } from "../../models.ts";
import style from "./style.css?inline";

@tag("nw-section-pro-paywall")
export class NWSectionProPaywall extends PureComponent<ProVariantProps> {
  static css = [tailwind, globalStyles, style];

  private content = networkSignal<string | undefined>({ default: undefined });
  private sectionStatus = signal<NWSectionStatus>(NWSectionStatus.LOCKED);

  install() {
    if (this.sectionStatus.peek() !== this.props.status) {
      this.sectionStatus.value = this.props.status;
    }
  }

  handleOnClick = async () => {
    if (!Store.user.isLoggedIn.value) {
      openLoginModal();
      return;
    }

    if (!Store.user.isPro()) {
      openProModal("secret_mission_unlock");
      return;
    }

    await this.fetchContentAndUnlock();

    import("@/services/analyticsService").then(({ useAnalyticsService }) => {
      useAnalyticsService().trackProSectionUnlocked(this.props.id);
    });
  };

  private fetchContentAndUnlock = async () => {
    if (this.props.contentId == null || Store.project.id.value == null || Store.project.getSelectedTrackId() == null) {
      return;
    }

    const controller = useEditorController("content");
    if (controller) {
      const res = await this.content.run(this.GETProContent);

      if (res) {
        controller.setSectionContent(this.props.id, res);
        controller.unlockProSection(this.props.id);
        useValidations().fetch(Store.project.id.value);

        setTimeout(() => {
          triggerToast({ message: "PRO Section unlocked! 🚀", position: "top", duration: 10000 });
        }, 200);
      }
    }
  };

  @bind
  async GETProContent(): Promise<string | undefined> {
    const api = initApi();

    if (!Store.project.id.value || !this.props.contentId) {
      return undefined;
    }

    const res = await api.getLockedProjectFileRaw({ id: Store.project.id.value, filename: this.props.contentId });

    if (res.raw.ok) {
      return await res.value();
    } else {
      const error = new Error(`Failed to fetch PRO content file: ${this.props.contentId}`);
      ErrorService.report(error);
      throw error;
    }
  }

  private getUnlockText() {
    if (Store.user.isPro()) {
      return "You're PRO! Jump in";
    }

    return Store.user.isLoggedIn.value ? "Unlock with PRO" : "Login to Unlock";
  }

  render() {
    const hasError = !!this.content.signal.error.value;

    return (
      <div id={this.props.id} class="relative h-full mt-48 mb-12 mx-1 animate-fadeInFast">
        <div
          class={classNames(
            "flex flex-col items-center justify-center gap-6 pt-40 rounded-[40px] w-full relative pro-section-background",
            {
              "pb-6": hasError,
              "pb-12": !hasError,
            },
          )}
        >
          <div class="absolute -top-36">
            {/*<NWRive*/}
            {/*  src="/static/animations/rive/keyhole.gif"*/}
            {/*  animation="Idle"*/}
            {/*  width={194}*/}
            {/*  height={330}*/}
            {/*  fit={Fit.Contain}*/}
            {/*  alignment={Alignment.Center}*/}
            {/*/>*/}
            <img
              class="w-full h-full max-h-[320px] object-contain"
              src="/static/keyhole-black.svg"
              alt="keyhole"
              width={194}
              height={330}
              style={{
                objectPosition: "center",
              }}
            />
          </div>

          <div class="text-center w-2/3 mx-auto space-y-4 z-[1]">
            <h2 className="text-2xl font-semibold text-primary py-0 pt-12">🤫 Secret Missions</h2>
            {/* TODO check this*/}
            <p className="text-tertiary text-sm">
              Ready for a challenge? Secret Missions are for students looking to showcase more advanced skills.
            </p>
          </div>

          <div
            class={classNames("flex flex-col px-6 items-center justify-center mb-6 space-y-4", {
              "mb-0": hasError,
              "mb-6": !hasError,
            })}
          >
            <NWButton
              size="lg"
              variant="primary"
              class="z-[1]"
              onClick={this.handleOnClick}
              loading={this.content.signal.isLoading.value}
              loadingText={this.getUnlockText()}
            >
              {this.getUnlockText()}
            </NWButton>
            {hasError && <NWError error={this.content.signal.error.value} />}
          </div>
        </div>
      </div>
    );
  }
}
